/**
 * Tool Registry
 * Manages agentic tools with permission model
 */

/**
 * @typedef {Object} Tool
 * @property {string} name
 * @property {string} description
 * @property {'low' | 'medium' | 'high'} risk
 * @property {Object} parameters - JSON Schema for parameters
 * @property {(params: Object) => Promise<any>} execute
 */

class ToolRegistry {
  constructor() {
    /** @type {Map<string, Tool>} */
    this.tools = new Map();

    /** @type {Map<string, 'allow' | 'ask' | 'deny'>} */
    this.permissions = new Map();

    this.registerBuiltinTools();
  }

  registerBuiltinTools() {
    this.register({
      name: 'file_read',
      description: 'Read contents of a file',
      risk: 'low',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'File path to read' },
        },
        required: ['path'],
      },
      execute: async ({ path }) => {
        // Simulated - in production this would go through a secure backend
        return { type: 'text', content: `[File read simulation] Contents of ${path}` };
      },
    });

    this.register({
      name: 'file_write',
      description: 'Write content to a file',
      risk: 'medium',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'File path to write' },
          content: { type: 'string', description: 'Content to write' },
        },
        required: ['path', 'content'],
      },
      execute: async ({ path, content }) => {
        return { type: 'text', content: `[File write simulation] Wrote ${content.length} chars to ${path}` };
      },
    });

    this.register({
      name: 'http_request',
      description: 'Make an HTTP request',
      risk: 'medium',
      parameters: {
        type: 'object',
        properties: {
          url: { type: 'string', description: 'URL to request' },
          method: { type: 'string', enum: ['GET', 'POST', 'PUT', 'DELETE'] },
          body: { type: 'string', description: 'Request body (JSON)' },
        },
        required: ['url'],
      },
      execute: async ({ url, method = 'GET', body }) => {
        try {
          const options = { method };
          if (body) {
            options.headers = { 'Content-Type': 'application/json' };
            options.body = body;
          }
          const response = await fetch(url, options);
          const text = await response.text();
          return {
            type: 'text',
            content: text.slice(0, 5000),
            metadata: { status: response.status, url },
          };
        } catch (error) {
          return { type: 'error', content: error.message };
        }
      },
    });

    this.register({
      name: 'terminal',
      description: 'Execute a terminal command',
      risk: 'high',
      parameters: {
        type: 'object',
        properties: {
          command: { type: 'string', description: 'Command to execute' },
          cwd: { type: 'string', description: 'Working directory' },
        },
        required: ['command'],
      },
      execute: async ({ command }) => {
        // Would need a backend for real execution
        return { type: 'text', content: `[Terminal simulation] Would execute: ${command}` };
      },
    });

    this.register({
      name: 'browser',
      description: 'Navigate to a URL and extract content',
      risk: 'high',
      parameters: {
        type: 'object',
        properties: {
          url: { type: 'string', description: 'URL to navigate to' },
          action: { type: 'string', enum: ['read', 'screenshot'] },
        },
        required: ['url'],
      },
      execute: async ({ url }) => {
        return { type: 'text', content: `[Browser simulation] Would navigate to: ${url}` };
      },
    });

    this.register({
      name: 'canvas',
      description: 'Create or update a canvas (code, diagram, table, form)',
      risk: 'low',
      parameters: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: ['code', 'diagram', 'table', 'form'] },
          title: { type: 'string' },
          content: { type: 'string' },
          language: { type: 'string', description: 'Programming language (for code type)' },
        },
        required: ['type', 'content'],
      },
      execute: async (params) => {
        return { type: 'canvas', ...params };
      },
    });
  }

  register(tool) {
    this.tools.set(tool.name, tool);
    // Default permissions based on risk
    if (!this.permissions.has(tool.name)) {
      this.permissions.set(tool.name, tool.risk === 'low' ? 'allow' : 'ask');
    }
  }

  get(name) {
    return this.tools.get(name);
  }

  getAll() {
    return [...this.tools.values()];
  }

  /**
   * Execute a tool with permission checking
   * @returns {{ result: any, approved: boolean }}
   */
  async execute(name, params) {
    const tool = this.tools.get(name);
    if (!tool) throw new Error(`Unknown tool: ${name}`);

    const permission = this.permissions.get(name);
    if (permission === 'deny') {
      return { result: null, approved: false, reason: 'denied' };
    }

    if (permission === 'ask') {
      // Return pending state - UI will handle approval
      return { result: null, approved: false, reason: 'needs_approval', tool, params };
    }

    const result = await tool.execute(params);
    return { result, approved: true };
  }

  setPermission(toolName, permission) {
    this.permissions.set(toolName, permission);
  }

  getPermission(toolName) {
    return this.permissions.get(toolName) || 'ask';
  }
}

export const tools = new ToolRegistry();
